import React, { useState } from 'react';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';
import { Icon } from '../../concepts/resources/Icon';
import { MenuButton } from '../../ui/MenuButton';
import { FadeIn } from '../../ui/Transitions';
import { SortMode } from '../common/Sorter';
import { BoardModel, FolderModel } from '../dashboard/Models';
import { BoardGrid } from '../dashboard/elements/BoardGrid';
import { FolderList } from '../dashboard/elements/FolderList';
import { Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Input, Menu } from './../../ui';
import { FolderBoardTable } from './FolderBoardTable';


const NoIcon = styled.div`
    width: 1.25rem;
    height: 1.25rem;
`;

const layoutOpts: any = [
    ['hybrid', 'Hybrid View'],
    ['grid', 'Grid View'],
    ['list', 'List View'],
];

const layoutIcon = (layout: any) => `deqo://icons/dash/${(layout ?? [])[0] || 'hybrid'}-view`;

const sortPropOpts: any = [
    ['label', 'Alphabetical'],
    ['createdAt', 'Date created'],
    ['updatedAt', 'Last updated'],
];

const sortOrderOpts: any = [
    ['asc', 'Ascending'],
    ['desc', 'Descending'],
];

type CommonProps = { actions?: boolean | Record<string, boolean> };
type DataProps = { readonly folders?: readonly FolderModel[], readonly boards?: readonly BoardModel[] };
type LayoutParamProps = { sort: SortMode, filter?: string };
type LayoutProps = CommonProps & DataProps & LayoutParamProps;

const GridLayout = React.memo<LayoutProps & { hybrid?: boolean }>(({ actions, hybrid, folders, boards, sort, filter }) =>
    <FadeIn>
        {folders && !!folders.length && <FolderList {...{ gridLike: !hybrid, folders, sort, filter }} />}
        {folders && !!folders.length && <Spacer f={2} />}
        {boards && !!boards.length && <BoardGrid {...{ actions, boards, sort, filter }} />}
    </FadeIn>
);

const TableLayout = React.memo<LayoutProps>(props =>
    <FadeIn>
        <FolderBoardTable {...props} />
    </FadeIn>
);

export type FolderBoardViewProps = CommonProps & DataProps;
export const FolderBoardView = React.memo<FolderBoardViewProps>(props => {

    const [layoutOpen, setLayoutOpen] = useState(false);
    const [layout, setLayout] = useLocalStorage<[string, string]>('deqo/ui/workspaceLayoutMode', layoutOpts[1]);
    const [sortOpen, setSortOpen] = useState(false);
    const [sortProp, setSortProp] = useLocalStorage<[string, string]>('deqo/ui/workspaceSortProp', sortPropOpts[2]);
    const [sortOrder, setSortOrder] = useLocalStorage<[string, string]>('deqo/ui/workspaceSortOrder', sortOrderOpts[1]);
    const [filter, setFilter] = useState('');
    const sort = [sortProp[0], sortOrder[0]] as SortMode;

    return <>
        <Flexer flex="row" align="center" justify="space-between" margin="0 0 3 0">
            <Flexer width="20">
                <Input autoFocus placeholder="Filter" value={filter} onChange={setFilter} />
            </Flexer>
            <Flexer flex="row">
                <MenuButton label={sortProp[1]} open={sortOpen} setOpen={setSortOpen}>
                    <Menu>
                        {sortPropOpts.map(x =>
                            <Menu.Item key={`${x[0]}.${x[1]}`} onClick={() => setSortProp(x)}>
                                {sortProp[0] === x[0] && <Icon size="1.25" src="deqo://icons/hero/check" />}
                                {sortProp[0] !== x[0] && <NoIcon />}
                                {x[1]}
                            </Menu.Item>
                        )}
                        <Menu.Divide />
                        {sortOrderOpts.map(x =>
                            <Menu.Item key={`${x[0]}.${x[1]}`} onClick={() => setSortOrder(x)}>
                                {sortOrder[0] === x[0] && <Icon size="1.25" src="deqo://icons/hero/check" />}
                                {sortOrder[0] !== x[0] && <NoIcon />}
                                {x[1]}
                            </Menu.Item>
                        )}
                    </Menu>
                </MenuButton>
                <Spacer mode="x" f={1} />
                <MenuButton icon={layoutIcon(layout)} open={layoutOpen} setOpen={setLayoutOpen}>
                    <Menu>
                        {layoutOpts.map(x =>
                            <Menu.Item key={`${x[0]}.${x[1]}`} onClick={() => setLayout(x)}>
                                {layout[0] === x[0] && <Icon size="1.25" src="deqo://icons/hero/check" />}
                                {layout[0] !== x[0] && <NoIcon />}
                                {x[1]}
                            </Menu.Item>
                        )}
                    </Menu>
                </MenuButton>
            </Flexer>
        </Flexer>
        <Spacer />
        {(layout ?? [])[0] === 'hybrid' && <GridLayout {... { hybrid: true, sort, filter }} {...props} />}
        {(layout ?? [])[0] === 'grid' && <GridLayout {... { sort, filter }} {...props} />}
        {(layout ?? [])[0] === 'list' && <TableLayout {... { sort, filter }} {...props} />}
    </>;
});
